import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { PiClockCounterClockwise } from "react-icons/pi";
import { HiOutlineX } from "react-icons/hi";
import API from "../../api";
import { capture } from "../../instrument";
import { getHiddenCodes } from "../../queries/hiddenCode";

const HiddenCodesSection = () => {
  const queryClient = useQueryClient();
  const [newCode, setNewCode] = useState("");

  const { data: hiddenCodes, isError } = useQuery({ queryKey: ["hidden-codes"], queryFn: getHiddenCodes });

  const refetchWhenLastMutationSettles = () => {
    if (queryClient.isMutating({ mutationKey: ["hidden-codes"] }) === 1) return queryClient.invalidateQueries({ queryKey: ["hidden-codes"] });
  };

  const hideMutation = useMutation({
    mutationKey: ["hidden-codes"],
    mutationFn: (code) => API.post("/hidden-code", { code }),
    onMutate: async (code) => {
      await queryClient.cancelQueries({ queryKey: ["hidden-codes"] });
      queryClient.setQueryData(["hidden-codes"], (cached) => [...cached, { code }]);
    },
    onError: (error, code) => {
      capture(error);
      toast.error(error.message || "Impossible de masquer ce code");
      setNewCode(code);
    },
    onSettled: refetchWhenLastMutationSettles,
  });

  const unhideMutation = useMutation({
    mutationKey: ["hidden-codes"],
    mutationFn: (id) => API.delete(`/hidden-code/${id}`),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ["hidden-codes"] });
      queryClient.setQueryData(["hidden-codes"], (cached) => cached.filter((hiddenCode) => hiddenCode._id !== id));
    },
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Impossible de démasquer ce code");
    },
    onSettled: refetchWhenLastMutationSettles,
  });

  const onSubmit = (event) => {
    event.preventDefault();
    const code = newCode.trim();
    if (!code) return;
    setNewCode("");
    hideMutation.mutate(code);
  };

  return (
    <section>
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-[15px] font-medium text-gray-900">Codes masqués</h3>
          <p className="mt-1 text-sm text-gray-500">
            Codes que votre équipe saisit dans le SIL sans que ce soit un examen (commentaire, tube, refus…). Sur les prochaines ordonnances, les ajouter ou les retirer ne compte
            plus comme une correction dans les statistiques, et ils ne sont jamais proposés en suggestion. Saisissez le code exactement comme dans le SIL, majuscules comprises.
          </p>
        </div>
        <button
          type="button"
          className="shrink-0 rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
          onClick={() => window.open("/audit-log/hiddencodes", "_blank", "noopener,noreferrer,width=1000,height=800")}
          title="Journal d'audit"
        >
          <PiClockCounterClockwise className="h-5 w-5" />
        </button>
      </div>

      {isError && <p className="text-sm text-gray-500">Impossible de charger les codes masqués.</p>}

      {!hiddenCodes && !isError && (
        <div className="flex justify-center py-16">
          <div className="size-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
        </div>
      )}

      {hiddenCodes && (
        <>
          <form
            onSubmit={onSubmit}
            className="flex flex-wrap items-center gap-1.5 rounded-xl bg-white p-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-gray-950/[0.06] focus-within:ring-2 focus-within:ring-primary-500/30"
          >
            {hiddenCodes.map((hiddenCode) => (
              <span key={hiddenCode._id || hiddenCode.code} className="inline-flex h-7 items-center gap-0.5 rounded-md bg-gray-100 pl-2.5 pr-1 font-mono text-[13px] text-gray-800">
                {hiddenCode.code}
                <button
                  type="button"
                  onClick={() => unhideMutation.mutate(hiddenCode._id)}
                  disabled={!hiddenCode._id}
                  title="Ne plus masquer"
                  aria-label={`Ne plus masquer ${hiddenCode.code}`}
                  className="rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700"
                >
                  <HiOutlineX className="size-3.5" />
                </button>
              </span>
            ))}
            <input
              type="text"
              aria-label="Code à masquer"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              placeholder={hiddenCodes.length > 0 ? "Ajouter un code…" : "Ex. : COMM, REFUS"}
              className="h-7 min-w-[12rem] flex-1 bg-transparent px-1.5 font-mono text-[13px] text-gray-900 outline-none placeholder:font-sans placeholder:text-gray-400"
            />
          </form>
          <p className="mt-2 px-1 text-xs text-gray-500">
            {hiddenCodes.length} code{hiddenCodes.length > 1 ? "s" : ""} · Entrée pour ajouter, × pour ne plus masquer
          </p>
        </>
      )}
    </section>
  );
};

export default HiddenCodesSection;
